import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuthStore } from '../store/authStore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  getAdminDashboardStats, 
  AdminDashboardStats, 
  getPendingDocuments, 
  updateDocumentStatus,
  PendingDocument,
  PendingBindingRequest,
  getPendingBindingRequests,
  updateBindingRequestStatus
} from '../lib/supabase/api/admin-service';
import { fetchPrintJobs, PrintJob } from '../lib/supabase/api/fetch-print-jobs';
import { Eye, Download, Printer, FileText, Copy, Search, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Input } from '../components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import KYCVerificationPage from './KYCVerificationPage';
import ShopsAnalyticsPage from './ShopsAnalyticsPage';
import ShopManagementPage from './ShopManagementPage'; // Add this import

const AdminPanel: React.FC = () => {
  const { user, isAdmin, loading } = useAuthStore();
  const navigate = useNavigate();
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  
  // Update AdminDashboardStats interface
  const [dashboardStats, setDashboardStats] = useState<AdminDashboardStats>({
    users_count: 0,
    documents_count: 0,
    pending_approvals: 0,
    pending_binding_requests: 0,
    total_revenue: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [pendingDocuments, setPendingDocuments] = useState<PendingDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [processingDocId, setProcessingDocId] = useState<string | null>(null);
  const [printJobs, setPrintJobs] = useState<PrintJob[]>([]);
  const [printJobsLoading, setPrintJobsLoading] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10); // 10 items per page
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  // Add new state for binding requests
  const [bindingRequests, setBindingRequests] = useState<PendingBindingRequest[]>([]);
  const [bindingRequestsLoading, setBindingRequestsLoading] = useState(false);
  const [processingBindingId, setProcessingBindingId] = useState<string | null>(null);

  // Add new pagination state for binding requests
  const [bindingCurrentPage, setBindingCurrentPage] = useState(1);
  const [bindingPageSize] = useState(10); // 10 items per page
  
  // Add binding search and filter state
  const [bindingSearchQuery, setBindingSearchQuery] = useState('');
  const [bindingStatusFilter, setBindingStatusFilter] = useState('all');

  // Filtered and paginated jobs
  const filteredJobs = printJobs.filter(job => {
    const matchesSearch = searchQuery === '' || 
      job.user_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.filename.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.shop_name.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
    
    let matchesDate = true;
    const jobDate = new Date(job.date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    const lastMonth = new Date(today);
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    
    if (dateFilter === 'today') {
      matchesDate = jobDate.toDateString() === today.toDateString();
    } else if (dateFilter === 'yesterday') {
      matchesDate = jobDate.toDateString() === yesterday.toDateString();
    } else if (dateFilter === 'last7days') {
      matchesDate = jobDate >= lastWeek;
    } else if (dateFilter === 'last30days') {
      matchesDate = jobDate >= lastMonth;
    }
    
    return matchesSearch && matchesStatus && matchesDate;
  });
  
  const totalPages = Math.ceil(filteredJobs.length / pageSize);
  const paginatedJobs = filteredJobs.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );
  
  // Filtered and paginated binding requests
  const filteredBindingRequests = bindingRequests.filter(request => {
    const matchesSearch = bindingSearchQuery === '' || 
      request.name.toLowerCase().includes(bindingSearchQuery.toLowerCase()) ||
      request.file_name.toLowerCase().includes(bindingSearchQuery.toLowerCase()) ||
      request.roll_number.toLowerCase().includes(bindingSearchQuery.toLowerCase());
    
    const matchesStatus = bindingStatusFilter === 'all' || request.status === bindingStatusFilter;
    
    return matchesSearch && matchesStatus;
  });
  
  const bindingTotalPages = Math.ceil(filteredBindingRequests.length / bindingPageSize);
  const paginatedBindingRequests = filteredBindingRequests.slice(
    (bindingCurrentPage - 1) * bindingPageSize,
    bindingCurrentPage * bindingPageSize
  );
  
  // Redirect non-admin users
  useEffect(() => {
    if (!loading && !isAdmin && user) {
      console.log('AdminPanel: User is not an admin, redirecting');
      navigate('/');
    }
  }, [user, isAdmin, loading, navigate]);
  
  // Fetch dashboard stats when component mounts
  useEffect(() => {
    const fetchStats = async () => {
      if (isAdmin) {
        setStatsLoading(true);
        try {
          const stats = await getAdminDashboardStats();
          console.log('Fetched admin stats:', stats);
          setDashboardStats(stats);
        } catch (error) {
          console.error('Error fetching admin stats:', error);
        } finally {
          setStatsLoading(false);
        }
      }
    };
    
    fetchStats();
  }, [isAdmin]);
  
  // Fetch print jobs when the component mounts
  useEffect(() => {
    if (isAdmin && !loading) {
      fetchAllPrintJobs();
    }
  }, [isAdmin, loading]);
  
  // Fetch pending documents when approvals tab is active
  const fetchPendingDocuments = async () => {
    console.log('Starting to fetch pending documents...');
    setDocumentsLoading(true);
    try {
      const documents = await getPendingDocuments();
      console.log('Fetched pending documents successfully:', documents);
      setPendingDocuments(documents);
    } catch (error: any) {
      const errorMessage = error?.message || 'Unknown error';
      const errorCode = error?.code || 'No error code';
      console.error('Error fetching pending documents:', { errorMessage, errorCode, error });
      
      // More detailed error toast
      toast.error(`Failed to load pending documents: ${errorMessage} (${errorCode})`);
    } finally {
      setDocumentsLoading(false);
    }
  };

  // Fetch print jobs
  const fetchAllPrintJobs = async () => {
    console.log('Starting to fetch print jobs...');
    setPrintJobsLoading(true);
    try {
      const jobs = await fetchPrintJobs();
      console.log('Fetched print jobs successfully:', jobs);
      setPrintJobs(jobs);
      // Reset to first page when fetching new data
      setCurrentPage(1);
    } catch (error: any) {
      const errorMessage = error?.message || 'Unknown error';
      const errorCode = error?.code || 'No error code';
      console.error('Error fetching print jobs:', { errorMessage, errorCode, error });
      
      // Error toast
      toast.error(`Failed to load print jobs: ${errorMessage} (${errorCode})`);
    } finally {
      setPrintJobsLoading(false);
    }
  };

  // Add function to fetch binding requests
  const fetchPendingBindingRequests = async () => {
    console.log('Starting to fetch pending binding requests...');
    setBindingRequestsLoading(true);
    try {
      const requests = await getPendingBindingRequests();
      console.log('Fetched pending binding requests successfully:', requests);
      setBindingRequests(requests);
      // Reset to first page when fetching new data
      setBindingCurrentPage(1);
    } catch (error: any) {
      const errorMessage = error?.message || 'Unknown error';
      const errorCode = error?.code || 'No error code';
      console.error('Error fetching pending binding requests:', { errorMessage, errorCode, error });
      
      // More detailed error toast
      toast.error(`Failed to load pending binding requests: ${errorMessage} (${errorCode})`);
    } finally {
      setBindingRequestsLoading(false);
    }
  };

  // Handle document approval/denial
  const handleStatusUpdate = async (documentId: string, status: 'approved' | 'denied') => {
    console.log(`Attempting to update document ${documentId} to status ${status}`);
    setProcessingDocId(documentId);
    try {
      await updateDocumentStatus(documentId, status);
      console.log(`Successfully updated document ${documentId} to ${status}`);
      
      // Update local state
      setPendingDocuments(prev => prev.filter(doc => doc.id !== documentId));
      
      // Update stats to reflect one less pending document
      setDashboardStats(prev => ({
        ...prev,
        pending_approvals: Math.max(0, prev.pending_approvals - 1)
      }));
      
      toast.success(`Document ${status === 'approved' ? 'approved' : 'denied'} successfully`);
    } catch (error: any) {
      const errorMessage = error?.message || 'Unknown error';
      const errorCode = error?.code || 'No error code';
      console.error(`Error updating document status:`, { errorMessage, errorCode, error });
      
      // More detailed error toast
      toast.error(`Failed to ${status} document: ${errorMessage} (${errorCode})`);
    } finally {
      setProcessingDocId(null);
    }
  };

  // Add function to handle binding request approval/completion
  const handleBindingStatusUpdate = async (requestId: string, status: 'completed' | 'cancelled') => {
    console.log(`Attempting to update binding request ${requestId} to status ${status}`);
    setProcessingBindingId(requestId);
    try {
      await updateBindingRequestStatus(requestId, status);
      console.log(`Successfully updated binding request ${requestId} to ${status}`);
      
      // Update local state
      setBindingRequests(prev => prev.filter(req => req.id !== requestId));
      
      // Update stats to reflect one less pending binding request
      setDashboardStats(prev => ({
        ...prev,
        pending_binding_requests: Math.max(0, prev.pending_binding_requests - 1)
      }));
      
      toast.success(`Binding request ${status === 'completed' ? 'completed' : 'cancelled'} successfully`);
    } catch (error: any) {
      const errorMessage = error?.message || 'Unknown error';
      const errorCode = error?.code || 'No error code';
      console.error(`Error updating binding request status:`, { errorMessage, errorCode, error });
      
      // More detailed error toast
      toast.error(`Failed to ${status} binding request: ${errorMessage} (${errorCode})`);
    } finally {
      setProcessingBindingId(null);
    }
  };

  // Handle tab change
  const handleTabChange = (value: string) => {
    console.log(`Tab changed to ${value}`);
    if (value === "approvals") {
      fetchPendingDocuments();
    } else if (value === "bindingRequests") {
      fetchPendingBindingRequests();
    } else if (value === "jobLogs") {
      // We don't need to fetch again here since we already fetch on component mount
      // But we can refresh the data if needed
      // fetchAllPrintJobs();
    }
  };
  
  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  
  // Handle binding page change
  const handleBindingPageChange = (page: number) => {
    setBindingCurrentPage(page);
  };
  
  // Reset filters
  const resetFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setDateFilter('all');
    setCurrentPage(1);
  };

  // Reset binding filters
  const resetBindingFilters = () => {
    setBindingSearchQuery('');
    setBindingStatusFilter('all');
    setBindingCurrentPage(1);
  };
  
  // Helper function to format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Only show loading state if we're loading AND we don't yet know if user is admin
  if (loading && !isAdmin) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
        <Footer />
      </div>
    );
  }
  
  // Show access denied only if we're sure the user is not an admin
  if (!isAdmin && !loading && user) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center p-6">
            <h2 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-4">You do not have permission to access the admin panel.</p>
            <Button onClick={() => navigate('/')}>Return to Home</Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-grow">
        <div className="container mx-auto px-3 md:px-4 py-6">
          <div className="flex flex-col space-y-6">
            {/* Admin Header */}
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Admin Panel</h1>
              <p className="text-gray-600">Manage your application settings and data</p>
            </div>
            
            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-6">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-500">Total Users</span>
                    {statsLoading ? (
                      <div className="h-8 w-16 bg-gray-200 animate-pulse rounded mt-1"></div>
                    ) : (
                      <span className="text-3xl font-bold text-gray-800">{dashboardStats.users_count}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-500">Total Documents</span>
                    {statsLoading ? (
                      <div className="h-8 w-16 bg-gray-200 animate-pulse rounded mt-1"></div>
                    ) : (
                      <span className="text-3xl font-bold text-gray-800">{dashboardStats.documents_count}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-500">Pending Approvals</span>
                    {statsLoading ? (
                      <div className="h-8 w-16 bg-gray-200 animate-pulse rounded mt-1"></div>
                    ) : (
                      <span className="text-3xl font-bold text-orange-500">{dashboardStats.pending_approvals}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-500">Pending Binding Requests</span>
                    {statsLoading ? (
                      <div className="h-8 w-16 bg-gray-200 animate-pulse rounded mt-1"></div>
                    ) : (
                      <span className="text-3xl font-bold text-purple-500" data-testid="binding-requests-count">
                        {dashboardStats.pending_binding_requests}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-500">Total Revenue</span>
                    {statsLoading ? (
                      <div className="h-8 w-16 bg-gray-200 animate-pulse rounded mt-1"></div>
                    ) : (
                      <span className="text-3xl font-bold text-green-600">₹{dashboardStats.total_revenue.toFixed(2)}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Admin Tabs */}
            <Tabs defaultValue="jobLogs" onValueChange={handleTabChange}>
              <div className="overflow-x-auto">
                <TabsList className="mb-4 w-full">
                  <TabsTrigger value="jobLogs" className="flex-1">Job Logs</TabsTrigger>
                  <TabsTrigger value="documents" className="flex-1">Documents</TabsTrigger>
                  <TabsTrigger value="approvals" className="flex-1">Approvals</TabsTrigger>
                  <TabsTrigger value="bindingRequests" className="flex-1">Spiral Bind Requests</TabsTrigger>
                  <TabsTrigger value="kycVerification" className="flex-1">KYC Verification</TabsTrigger>
                  <TabsTrigger value="shopsAnalytics" className="flex-1">Shops Analytics</TabsTrigger>
                  <TabsTrigger value="shopManagement" className="flex-1">Shop Management</TabsTrigger> {/* Add this line */}
                  <TabsTrigger value="settings" className="flex-1">Settings</TabsTrigger>
                </TabsList>
              </div>
              
              <TabsContent value="jobLogs">
                <Card>
                  <CardHeader>
                    <CardTitle>Print Job Logs</CardTitle>
                    <CardDescription>View and manage print job history</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Search and Filter Controls */}
                    <div className="mb-6 space-y-4">
                      <div className="flex flex-col md:flex-row gap-4">
                        <div className="relative w-full md:w-1/3">
                          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
                          <Input
                            placeholder="Search by user or filename..."
                            className="pl-9"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                          />
                        </div>
                        
                        <div className="w-full md:w-1/4">
                          <Select 
                            value={statusFilter} 
                            onValueChange={setStatusFilter}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Filter by status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Statuses</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="w-full md:w-1/4">
                          <Select 
                            value={dateFilter} 
                            onValueChange={setDateFilter}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Filter by date" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Time</SelectItem>
                              <SelectItem value="today">Today</SelectItem>
                              <SelectItem value="yesterday">Yesterday</SelectItem>
                              <SelectItem value="last7days">Last 7 Days</SelectItem>
                              <SelectItem value="last30days">Last 30 Days</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <Button 
                          variant="outline" 
                          onClick={resetFilters}
                          className="w-full md:w-auto"
                        >
                          Reset Filters
                        </Button>
                      </div>
                      
                      {/* Results count */}
                      <div className="text-sm text-gray-500">
                        Showing {filteredJobs.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to {Math.min(currentPage * pageSize, filteredJobs.length)} of {filteredJobs.length} results
                      </div>
                    </div>
                    
                    {printJobsLoading ? (
                      <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                      </div>
                    ) : paginatedJobs.length > 0 ? (
                      <>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shop</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {paginatedJobs.map((job, index) => (
                                <tr key={index}>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">{job.user_name}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">{job.shop_name}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">{job.filename}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">₹{job.amount.toFixed(2)}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {new Date(job.date).toLocaleDateString()}
                                    <div className="text-xs text-gray-400">
                                      {new Date(job.date).toLocaleTimeString()}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                      job.status === 'completed' ? 'bg-green-100 text-green-800' :
                                      job.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                      job.status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                      {job.status.replace('_', ' ').charAt(0).toUpperCase() + job.status.replace('_', ' ').slice(1)}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    {job.file_url && (
                                      <a 
                                        href={job.file_url}
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center text-indigo-600 hover:text-indigo-900 mr-3"
                                      >
                                        <FileText size={16} className="mr-1" />
                                        View Document
                                      </a>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                          <div className="flex justify-center items-center space-x-2 mt-6">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                              disabled={currentPage === 1}
                            >
                              <ChevronLeft className="h-4 w-4" />
                              <span className="sr-only">Previous page</span>
                            </Button>
                            
                            {/* Page numbers */}
                            <div className="flex items-center space-x-1">
                              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                // Show pages around current page
                                let pageNum: number;
                                if (totalPages <= 5) {
                                  // If less than 5 pages, show all
                                  pageNum = i + 1;
                                } else if (currentPage <= 3) {
                                  // If near start, show first 5
                                  pageNum = i + 1;
                                } else if (currentPage >= totalPages - 2) {
                                  // If near end, show last 5
                                  pageNum = totalPages - 4 + i;
                                } else {
                                  // Otherwise show current and neighbors
                                  pageNum = currentPage - 2 + i;
                                }
                                
                                return (
                                  <Button
                                    key={pageNum}
                                    variant={currentPage === pageNum ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => handlePageChange(pageNum)}
                                    className={currentPage === pageNum ? "bg-blue-500" : ""}
                                  >
                                    {pageNum}
                                  </Button>
                                );
                              })}
                            </div>
                            
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                              disabled={currentPage === totalPages}
                            >
                              <ChevronRight className="h-4 w-4" />
                              <span className="sr-only">Next page</span>
                            </Button>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-gray-600 text-center py-8">
                        {printJobs.length > 0 
                          ? "No print jobs match your filters. Try adjusting your search criteria." 
                          : "No print jobs found."}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="documents">
                <Card>
                  <CardHeader>
                    <CardTitle>Document Management</CardTitle>
                    <CardDescription>Manage all uploaded documents</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">No documents found. Document management will be implemented in a future update.</p>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="approvals">
                <Card>
                  <CardHeader>
                    <CardTitle>Document Approvals</CardTitle>
                    <CardDescription>Review and approve submitted documents</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {documentsLoading ? (
                      <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                      </div>
                    ) : pendingDocuments.length > 0 ? (
                      <div className="space-y-6">
                        {pendingDocuments.map((doc) => (
                          <Card key={doc.id} className="overflow-hidden">
                            <div className="flex flex-col md:flex-row">
                              {/* Document Cover Image */}
                              <div className="w-full md:w-1/4 p-4 flex items-center justify-center bg-gray-100">
                                {doc.cover_image_path ? (
                                  <img 
                                    src={doc.cover_image_path} 
                                    alt={doc.title} 
                                    className="max-h-48 object-contain"
                                  />
                                ) : (
                                  <div className="w-full h-48 bg-gray-200 flex items-center justify-center text-gray-400">
                                    No cover image
                                  </div>
                                )}
                              </div>
                              
                              {/* Document Details */}
                              <div className="w-full md:w-3/4 p-4">
                                <div className="flex flex-col space-y-4">
                                  <div>
                                    <h3 className="text-xl font-semibold text-gray-800">
                                      <a 
                                        href={doc.file_path} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:underline"
                                      >
                                        {doc.title}
                                      </a>
                                    </h3>
                                    <p className="text-sm text-gray-500">
                                      Submitted by {doc.user_email} • {new Date(doc.created_at).toLocaleDateString()}
                                    </p>
                                  </div>
                                  
                                  {doc.description && (
                                    <p className="text-gray-600 text-sm">{doc.description}</p>
                                  )}
                                  
                                  <div className="flex flex-wrap gap-2">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-gray-100 text-gray-800 border border-gray-200">
                                      {doc.file_type}
                                    </span>
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-gray-100 text-gray-800 border border-gray-200">
                                      {formatFileSize(doc.file_size)}
                                    </span>
                                    <div className="flex items-center space-x-1 text-gray-500 text-xs">
                                      <Eye size={14} />
                                      <span>{doc.views}</span>
                                    </div>
                                    <div className="flex items-center space-x-1 text-gray-500 text-xs">
                                      <Download size={14} />
                                      <span>{doc.downloads}</span>
                                    </div>
                                  </div>
                                  
                                  <div className="flex gap-2 mt-2">
                                    <Button
                                      variant="default"
                                      size="sm"
                                      onClick={() => handleStatusUpdate(doc.id, 'approved')}
                                      disabled={processingDocId === doc.id}
                                      className="bg-green-600 hover:bg-green-700"
                                    >
                                      {processingDocId === doc.id ? 'Processing...' : 'Approve'}
                                    </Button>
                                    <Button
                                      variant="destructive"
                                      size="sm"
                                      onClick={() => handleStatusUpdate(doc.id, 'denied')}
                                      disabled={processingDocId === doc.id}
                                    >
                                      {processingDocId === doc.id ? 'Processing...' : 'Deny'}
                                    </Button>
                                    <a 
                                      href={doc.file_path}
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                      Preview File
                                    </a>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-600 text-center py-8">No pending approvals found.</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="bindingRequests">
                <Card>
                  <CardHeader>
                    <CardTitle>Spiral Binding Requests</CardTitle>
                    <CardDescription>Process spiral binding requests from users</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Search and Filter Controls */}
                    <div className="mb-6 space-y-4">
                      <div className="flex flex-col md:flex-row gap-4">
                        <div className="relative w-full md:w-1/3">
                          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
                          <Input
                            placeholder="Search by name, roll number, or filename..."
                            className="pl-9"
                            value={bindingSearchQuery}
                            onChange={(e) => setBindingSearchQuery(e.target.value)}
                          />
                        </div>
                    
                        <Button 
                          variant="outline" 
                          onClick={resetBindingFilters}
                          className="w-full md:w-auto"
                        >
                          Reset Filters
                        </Button>
                      </div>
                      
                      {/* Results count */}
                      <div className="text-sm text-gray-500">
                        Showing {filteredBindingRequests.length > 0 ? (bindingCurrentPage - 1) * bindingPageSize + 1 : 0} to {Math.min(bindingCurrentPage * bindingPageSize, filteredBindingRequests.length)} of {filteredBindingRequests.length} results
                      </div>
                    </div>
                    
                    {bindingRequestsLoading ? (
                      <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                      </div>
                    ) : paginatedBindingRequests.length > 0 ? (
                      <>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pages</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {paginatedBindingRequests.map((request) => (
                                <tr key={request.id}>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">{request.name}</div>
                                    <div className="text-sm text-gray-500">{request.roll_number}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">
                                      {request.file_name || "Not uploaded"}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {request.file_name ? formatFileSize(request.file_size) : "No file"}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">
                                      {request.file_name ? request.page_count : "-"}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-500">
                                      <div>Room: {request.room_number}</div>
                                      <div>Phone: {request.phone_number}</div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {new Date(request.created_at).toLocaleDateString()}
                                    <div className="text-xs text-gray-400">
                                      {new Date(request.created_at).toLocaleTimeString()}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                      request.status === 'completed' ? 'bg-green-100 text-green-800' :
                                      request.status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                      {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <div className="flex flex-col gap-2">
                                      <button
                                        onClick={() => handleBindingStatusUpdate(request.id, 'completed')}
                                        disabled={processingBindingId === request.id}
                                        className="inline-flex items-center justify-center px-3 py-1 border border-green-600 text-xs font-medium rounded-md text-green-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-green-500 transition-colors disabled:opacity-50"
                                      >
                                        {processingBindingId === request.id ? 'Processing...' : 'Complete'}
                                      </button>
                                      <button
                                        onClick={() => handleBindingStatusUpdate(request.id, 'cancelled')}
                                        disabled={processingBindingId === request.id}
                                        className="inline-flex items-center justify-center px-3 py-1 border border-red-600 text-xs font-medium rounded-md text-red-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-red-500 transition-colors disabled:opacity-50"
                                      >
                                        {processingBindingId === request.id ? 'Processing...' : 'Cancel'}
                                      </button>
                                      {request.file_path ? (
                                        <button 
                                          onClick={(e) => {
                                            e.preventDefault();
                                            window.open(`${supabaseUrl}/storage/v1/object/public/binding-pdfs/${request.file_path}`, '_blank');
                                          }}
                                          className="inline-flex items-center justify-center px-3 py-1 border border-blue-600 text-xs font-medium rounded-md text-blue-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
                                        >
                                          View File
                                        </button>
                                      ) : (
                                        <button
                                          disabled
                                          className="inline-flex items-center justify-center px-3 py-1 border border-gray-300 text-xs font-medium rounded-md text-gray-400 bg-gray-50 cursor-not-allowed"
                                        >
                                          No File
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Pagination Controls */}
                        {bindingTotalPages > 1 && (
                          <div className="flex justify-center items-center space-x-2 mt-6">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleBindingPageChange(Math.max(1, bindingCurrentPage - 1))}
                              disabled={bindingCurrentPage === 1}
                            >
                              <ChevronLeft className="h-4 w-4" />
                              <span className="sr-only">Previous page</span>
                            </Button>
                            
                            {/* Page numbers */}
                            <div className="flex items-center space-x-1">
                              {Array.from({ length: Math.min(5, bindingTotalPages) }, (_, i) => {
                                // Show pages around current page
                                let pageNum: number;
                                if (bindingTotalPages <= 5) {
                                  // If less than 5 pages, show all
                                  pageNum = i + 1;
                                } else if (bindingCurrentPage <= 3) {
                                  // If near start, show first 5
                                  pageNum = i + 1;
                                } else if (bindingCurrentPage >= bindingTotalPages - 2) {
                                  // If near end, show last 5
                                  pageNum = bindingTotalPages - 4 + i;
                                } else {
                                  // Otherwise show current and neighbors
                                  pageNum = bindingCurrentPage - 2 + i;
                                }
                                
                                return (
                                  <Button
                                    key={pageNum}
                                    variant={bindingCurrentPage === pageNum ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => handleBindingPageChange(pageNum)}
                                    className={bindingCurrentPage === pageNum ? "bg-blue-500" : ""}
                                  >
                                    {pageNum}
                                  </Button>
                                );
                              })}
                            </div>
                            
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleBindingPageChange(Math.min(bindingTotalPages, bindingCurrentPage + 1))}
                              disabled={bindingCurrentPage === bindingTotalPages}
                            >
                              <ChevronRight className="h-4 w-4" />
                              <span className="sr-only">Next page</span>
                            </Button>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-gray-600 text-center py-8">
                        {bindingRequests.length > 0 
                          ? "No binding requests match your filters. Try adjusting your search criteria." 
                          : "No pending binding requests found."}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="kycVerification">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-4">
                    <KYCVerificationPage isEmbedded={true} />
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="shopsAnalytics">
                <ShopsAnalyticsPage />
              </TabsContent>
              
              <TabsContent value="shopManagement">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-4">
                    <ShopManagementPage isEmbedded={true} />
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="settings">
                <Card>
                  <CardHeader>
                    <CardTitle>System Settings</CardTitle>
                    <CardDescription>Configure application settings</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">Settings configuration will be implemented in a future update.</p>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AdminPanel;
